import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { sdk } from '@/lib/sdk'
import type { StorefrontConfig, StorefrontMode } from '@/types/storefront'
import { StorefrontProductPicker } from './StorefrontProductPicker'
import { loadAllProducts, loadPickablePlans, type PickablePlan, type PickableProduct } from './storefrontProducts'

interface StorefrontSettingsDialogProps {
  spaceId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: () => void
  /**
   * Starts from this selection instead of the saved one. The creation wizard
   * passes what the owner had picked when saving it failed, so retrying does
   * not mean picking everything again.
   */
  initialSelection?: StorefrontConfig
}

/**
 * What a store shows (F-29): every active product of the community, or a
 * chosen list in the order it was chosen. Owner or admin; the server checks.
 * F-29 phase 3: one-off products, whole plans and add-ons are picked in three
 * groups; a billing option is never picked alone.
 */
export function StorefrontSettingsDialog({ spaceId, open, onOpenChange, onSaved, initialSelection }: StorefrontSettingsDialogProps) {
  const { t } = useTranslation(['common'])
  const [mode, setMode] = useState<StorefrontMode>('all')
  const [picked, setPicked] = useState<string[]>([])
  const [pickedPlans, setPickedPlans] = useState<string[]>([])
  const [droppedOptionCount, setDroppedOptionCount] = useState(0)
  const [products, setProducts] = useState<PickableProduct[]>([])
  const [plans, setPlans] = useState<PickablePlan[]>([])
  const [loading, setLoading] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadFailed(false)
    setError(null)
    try {
      const [config, list] = await Promise.all([sdk.storefront.getConfig(spaceId), loadAllProducts()])
      const source = initialSelection ?? config
      setMode(source?.mode === 'selected' ? 'selected' : 'all')
      setPicked(Array.isArray(source?.productIds) ? source.productIds : [])
      setPickedPlans(Array.isArray(source?.planIds) ? source.planIds : [])
      setDroppedOptionCount(Number(source?.droppedOptionCount) || 0)
      setProducts(list)
      setPlans(await loadPickablePlans(list))
    } catch {
      setLoadFailed(true)
    } finally {
      setLoading(false)
    }
  }, [spaceId, initialSelection])

  useEffect(() => {
    if (open) void load()
  }, [open, load])

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      /* Only ids that still exist: a product or plan deleted since keeps nothing alive. */
      const known = new Set(products.map((product) => product.id))
      const knownPlans = new Set(plans.map((plan) => plan.id))
      await sdk.storefront.updateConfig(spaceId, {
        mode,
        productIds: picked.filter((id) => known.has(id)),
        planIds: pickedPlans.filter((id) => knownPlans.has(id))
      })
      onSaved()
      onOpenChange(false)
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } } | null)?.response?.data?.message
      setError(message || t('common:errors.generic'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="max-h-[90dvh] w-[calc(100%-1.5rem)] max-w-xl overflow-y-auto p-4 sm:p-6">
        <div className="min-w-0 space-y-5">
          <DialogHeader className="text-left">
            <DialogTitle>{t('common:storefront.settingsTitle')}</DialogTitle>
            <DialogDescription>{t('common:storefront.settingsDescription')}</DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="space-y-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded-lg bg-gray-100" />
              ))}
            </div>
          ) : loadFailed ? (
            <div className="flex flex-col items-center py-8 text-center">
              <AlertTriangle className="mb-3 h-8 w-8 text-amber-500" />
              <p className="text-sm text-gray-700">{t('common:storefront.loadFailed')}</p>
              <Button variant="outline" className="mt-3" onClick={() => void load()}>
                <RotateCcw className="mr-2 h-4 w-4" />
                {t('common:retry')}
              </Button>
            </div>
          ) : (
            <StorefrontProductPicker
              mode={mode}
              onModeChange={setMode}
              picked={picked}
              onPickedChange={setPicked}
              pickedPlans={pickedPlans}
              onPickedPlansChange={setPickedPlans}
              products={products}
              plans={plans}
              droppedOptionCount={droppedOptionCount}
              disabled={saving}
            />
          )}

          {error && (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => onOpenChange(false)} disabled={saving}>
              {t('common:cancel')}
            </Button>
            <Button
              variant="secondary"
              className="w-full sm:w-auto"
              onClick={() => void save()}
              disabled={saving || loading || loadFailed}
              data-testid="storefront-settings-save"
            >
              {saving ? t('common:saving') : t('common:save')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
